import {
  Dialog,
  DialogPanel,
  DialogTitle,
  Description,
  Transition,
  TransitionChild,
} from '@headlessui/react'
import _ from 'lodash'
import PropTypes from 'prop-types'
import React, { Fragment } from 'react'
import { twMerge as mergeClassNames } from 'tailwind-merge'

// Components
import { Button } from '../Button'

const Modal = ({
  actions = [
    { type: 'cancel', label: 'Cancel' },
    { type: 'submit', label: 'Save' },
  ],
  className,
  content,
  description = null,
  icon,
  loading = false,
  onClose = null,
  open = false,
  setOpen,
  title,
}) => {
  const renderActions = () => {
    const submit = _.find(actions, (a) => a.type === 'submit')
    const cancel = _.find(actions, (a) => a.type === 'cancel')
    const deleteAction = _.find(actions, (a) => a.type === 'delete')
    const extraActions = _.filter(actions, (a) => a.type === 'extra')

    if (!submit && !deleteAction) {
      return (
        <div className="mt-5 flex flex-row items-center sm:mt-6">
          <Button
            background="bg-white"
            fullWidth
            loading={loading}
            onClick={() => {
              if (cancel.onClick) cancel.onClick()
              if (setOpen) setOpen(false)
            }}
            outlined
            label={cancel.label}
          />

          {extraActions.map((action) => (
            <Button
              fullWidth
              onClick={action.onClick}
              label={action.label}
              loading={loading}
              disabled={action.disabled}
            />
          ))}
        </div>
      )
    }

    if (deleteAction) {
      return (
        <>
          <div className="mt-5 flex flex-row items-center space-y-3 sm:mt-6 sm:gap-3 sm:space-y-0">
            <Button
              background="bg-white"
              fullWidth
              loading={loading}
              onClick={() => {
                if (cancel.onClick) cancel.onClick()
                if (setOpen) setOpen(false)
              }}
              outlined
              label={cancel.label}
            />

            {extraActions.map((action) => (
              <Button
                fullWidth
                onClick={action.onClick}
                label={action.label}
                loading={loading}
                disabled={action.disabled}
              />
            ))}

            <Button fullWidth onClick={() => submit.onClick()} label={submit.label} />
          </div>

          <Button
            background="bg-red"
            fullWidth
            loading={loading}
            onClick={() => deleteAction.onClick()}
            label={deleteAction.label}
          />
        </>
      )
    }

    return (
      <div className="mt-5 flex flex-row items-center space-y-3 sm:mt-6 sm:gap-3 sm:space-y-0">
        <Button
          background="bg-white"
          fullWidth
          loading={loading}
          onClick={() => {
            if (cancel.onClick) cancel.onClick()
            if (setOpen) setOpen(false)
          }}
          outlined
          label={cancel.label}
        />

        {extraActions.map((action) => (
          <Button
            fullWidth
            onClick={action.onClick}
            label={action.label}
            loading={loading}
            disabled={action.disabled}
          />
        ))}

        <Button
          fullWidth
          onClick={submit.onClick}
          label={submit.label}
          loading={loading}
          disabled={submit.disabled}
        />
      </div>
    )
  }

  /**
   * Renders a Modal Description if one is provided.
   *
   * - If the description is an array, maps out the provided strings with vertical spacing.
   * - Otherwise, renders a single description.
   */
  const renderDescription = () => {
    if (description) {
      if (_.isArray(description))
        return (
          <div className="mt-4 space-y-2">
            {description.map((d) => (
              <Description className="text-sm font-normal text-gray-500">{d}</Description>
            ))}
          </div>
        )

      return (
        <Description className="my-4 text-sm font-normal text-gray-500">{description}</Description>
      )
    }

    return null
  }

  return (
    <Transition show={open} as={Fragment}>
      <Dialog
        as="div"
        className="relative z-50"
        onClose={() => {
          if (onClose) onClose()
          if (setOpen) setOpen(false)
        }}
      >
        <TransitionChild
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500/75 transition-opacity" />
        </TransitionChild>

        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <TransitionChild
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <DialogPanel
                className={mergeClassNames(
                  'relative w-full rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:p-6',
                  className || 'sm:max-w-[75vw]',
                )}
              >
                <div>
                  <div className="flex flex-row content-center items-center justify-center space-x-2">
                    <div className="flex size-12 items-center justify-center rounded-full bg-blue">
                      {icon}
                    </div>
                    <DialogTitle className="text-xl font-bold leading-6 text-gray-900">
                      {title}
                    </DialogTitle>
                  </div>

                  {renderDescription()}

                  {content}
                </div>

                <div className="space-y-3">{renderActions()}</div>
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </Transition>
  )
}

Modal.propTypes = {
  actions: PropTypes.arrayOf(PropTypes.object),
  className: PropTypes.string,
  content: PropTypes.node.isRequired,
  description: PropTypes.string,
  icon: PropTypes.node.isRequired,
  loading: PropTypes.bool,
  onClose: PropTypes.func,
  open: PropTypes.bool,
  setOpen: PropTypes.func,
  title: PropTypes.string.isRequired,
}

export default Modal
